import type { TraceEvent, TraceSpan } from "@artis/logger";

import type { InboundEvent, OutboundEvent } from "../lib/runtime";

import type { Transport } from "./types";

export * from "./multiplexer";
export * from "./swappable";
export * from "./adapter";
export * from "./types";

export interface CalcEventMap {
  "calc/inbound": InboundEvent;
  "calc/outbound": OutboundEvent;
  "log/event": TraceEvent;
  "log/span-enter": TraceSpan;
  "log/span-exit": TraceSpan;
  "grid/replicated": Uint8Array;
}

export type CalcMessage = { [K in keyof CalcEventMap]: { tag: K; data: CalcEventMap[K] } }[keyof CalcEventMap];

export class CalcChannels {
  private subs = new Map<string, Set<(data: unknown) => void>>();

  constructor(private transport: Transport) {
    this.transport.onMessage((event) => {
      if ("tag" in event) {
        this.subs.get(event.tag)?.forEach((cb) => cb(event.data));
      }
    });
  }

  send = <K extends keyof CalcEventMap>(tag: K, data: CalcEventMap[K], transfer?: Transferable[]) =>
    this.transport.send({ tag, data }, transfer);

  on = <K extends keyof CalcEventMap>(tag: K, cb: (event: CalcEventMap[K]) => void) => {
    if (!this.subs.has(tag as string)) this.subs.set(tag as string, new Set());
    const set = this.subs.get(tag as string)!;
    set.add(cb as (data: unknown) => void);
    return () => set.delete(cb as (data: unknown) => void);
  };

  channel = <K extends keyof CalcEventMap>(tag: K): Transport<CalcEventMap[K]> => ({
    send: (data, transfer) => this.send(tag, data, transfer),
    onMessage: (cb) => this.on(tag, cb),
  });
}
